import { inject } from '@angular/core';
import { CanMatchFn, Router } from '@angular/router';

/**
 * Where pages lived before the menu restructure, and where they live now. Both
 * sides are written without the `/pages` prefix every URL had back then;
 * `pagesPrefixRedirectMatch` strips it before looking an old URL up here.
 * Old bookmarks, Teams/e-mail buttons and stored notification links still use
 * the left-hand side, so these stay until those have aged out.
 *
 * Each entry maps a URL prefix: whatever follows it is carried over unchanged,
 * so one line covers a whole section. The longest matching prefix wins.
 */
export const LEGACY_URL_PREFIXES: ReadonlyArray<readonly [from: string, to: string]> = [
	['/support/erix', '/erix'],
	['/mapping/deal-manager', '/deal-manager'],
	['/mapping/deal-manager/sink-dlq', '/deal-manager/errors/sink'],
	['/mapping/deal-manager/event-dlq', '/deal-manager/errors/event'],
	['/mapping/deal-manager/dealmanager-dlq', '/deal-manager/errors/general'],
	['/template', '/nomination/template'],
	['/support/contacts/counterparty-details', '/nomination/contacts/counterparty'],
	['/support/contacts/tso-details', '/nomination/contacts/tso'],
	['/pnl', '/performance'],
	['/gas/long-term-overview', '/performance/gas-long-term'],
	['/gas/performance-overview', '/performance/gas-performance-overview'],
	['/mapping/transformation', '/data-platform/transformation'],
	['/mapping/transformation/mappings', '/data-platform/transformation/mapping'],
	['/mapping/transformation/dlq/state', '/data-platform/transformation/state'],
	['/reporting/data-platform/global-mapping-new', '/data-platform/global-mapping/curves'],
	['/reporting/data-platform/searches', '/data-platform/global-mapping/searches'],
	['/reporting/data-platform/global-mapping-config', '/data-platform/global-mapping/search-config'],
	// Older still: these were already redirects inside reporting.routes.ts.
	['/reporting/global-mapping/new', '/data-platform/global-mapping/curves'],
	['/reporting/global-mapping/searches', '/data-platform/global-mapping/searches'],
	['/reporting/global-mapping-config', '/data-platform/global-mapping/search-config'],
	['/support/data-hub', '/data-platform/schedulers'],
	['/support/data-hub/schedules', '/data-platform/schedulers/schedule-list'],
	['/support/data-hub/manual-product', '/data-platform/manual/insert'],
	['/support/data-hub/manual-product/template', '/data-platform/manual/insert-templates'],
	['/support/data-hub/manual-upload', '/data-platform/manual/upload'],
	['/mapping', '/old-data-platform/mapping'],
	['/mapping/map-trader-portfolio', '/old-data-platform/mapping/trader-portfolio'],
	['/mapping/map-counterparty-code', '/old-data-platform/mapping/counterparty-code'],
	['/mapping/map-grid-code', '/old-data-platform/mapping/grid-code'],
	['/mapping/map-tso', '/old-data-platform/mapping/tso'],
	['/reporting/global-mapping', '/old-data-platform/global-mapping'],
	['/reporting/powerbi', '/old-data-platform/power-bi'],
	['/gas/gas-maintenance', '/reporting/maintenance'],
	['/gas/gas-maintenance/overview', '/reporting/maintenance/gas'],
	['/gas/gas-maintenance/calendar', '/reporting/maintenance/gas-calendar'],
	['/gas/reports/storage-fill-level', '/reporting/gas-storage'],
	['/capacities', '/reporting/capacities'],
	['/capacities/capacities', '/reporting/capacities/list'],
	['/capacities/daily-capacities', '/reporting/capacities/daily'],
	['/renewable', '/reporting/renewable'],
	['/admin/notification-gateway', '/configuration/notification-gateway'],
	['/gas/tnp/position-overview', '/positions/gas']
];

/**
 * Old URLs that moved on their own while the pages below them stayed put, so a
 * prefix would drag the wrong pages along. Checked before the prefixes.
 */
export const LEGACY_URLS: Readonly<Record<string, string>> = {
	'/nomination': '/nomination/plans'
};

/** The current URL for a pre-restructure one, or `null` when it never moved. */
export function resolveLegacyUrl(url: string, prefixes = LEGACY_URL_PREFIXES, exact = LEGACY_URLS): string | null {
	if (Object.hasOwn(exact, url)) return exact[url];
	let best: readonly [string, string] | null = null;
	for (const entry of prefixes) {
		const [from] = entry;
		if ((url === from || url.startsWith(`${from}/`)) && (!best || from.length > best[0].length)) best = entry;
	}
	return best ? best[1] + url.slice(best[0].length) : null;
}

/**
 * Sits just before the not-found fallback, so it only ever sees URLs nothing
 * else matched. Query params and fragment are kept, which an absolute
 * `redirectTo` would drop.
 */
export const legacyRedirectMatch: CanMatchFn = (_route, segments) => {
	const target = resolveLegacyUrl(`/${segments.map(s => s.path).join('/')}`);
	return target ? redirectKeepingQuery(inject(Router), target) : false;
};

/**
 * Every page used to sit under `/pages`. An old link drops that prefix and goes
 * through the tables above in the same step, so even a pre-restructure link
 * lands with a single redirect.
 */
export const pagesPrefixRedirectMatch: CanMatchFn = (_route, segments) => {
	const url = `/${segments
		.slice(1)
		.map(s => s.path)
		.join('/')}`;
	return redirectKeepingQuery(inject(Router), resolveLegacyUrl(url) ?? url);
};

function redirectKeepingQuery(router: Router, target: string) {
	const current = router.getCurrentNavigation()?.extractedUrl;
	return router.createUrlTree([target], {
		queryParams: current?.queryParams,
		fragment: current?.fragment ?? undefined
	});
}
